import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, useCart } from '@/App';
import { toast } from 'sonner';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CartPage() {
  const { user, token } = useAuth();
  const { cart, loading, updateCartItem, removeFromCart, fetchCart } = useCart();

  const handleCheckout = async () => {
    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { origin_url: window.location.origin },
        { headers, withCredentials: true }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    }
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(productId);
    } else {
      await updateCartItem(productId, newQuantity);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="empty-cart">
        <ShoppingBag className="w-20 h-20 text-white/20 mb-6" />
        <h2 className="heading-display text-3xl text-white mb-4">Your Cart is Empty</h2>
        <p className="text-white/60 mb-8">Looks like you haven't added anything yet.</p>
        <Link to="/products">
          <Button className="btn-primary flex items-center gap-2" data-testid="continue-shopping-btn">
            Continue Shopping <ArrowRight className="w-5 h-5" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="cart-page">
      {/* Header */}
      <section className="py-12 border-b border-[#27272A]">
        <div className="container-main">
          <h1 className="heading-display text-4xl md:text-5xl text-white" data-testid="cart-title">
            Shopping Cart
          </h1>
          <p className="text-white/60 mt-2">
            {cart.items.length} {cart.items.length === 1 ? 'item' : 'items'}
          </p>
        </div>
      </section>

      {/* Cart Content */}
      <section className="section-gap">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-6 p-6 bg-[#0A0A0A] border border-[#27272A]"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <Link to={`/products/${item.product_id}`} className="shrink-0">
                    <div className="w-24 h-24 md:w-32 md:h-32 bg-[#111111] overflow-hidden">
                      <img
                        src={item.product?.images?.[0] || 'https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg'}
                        alt={item.product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.product_id}`}>
                      <h3 className="font-barlow font-bold text-lg text-white hover:text-[#FF3B30] transition-colors duration-200 line-clamp-2">
                        {item.product?.name}
                      </h3>
                    </Link>
                    
                    {item.product?.athlete && (
                      <p className="text-white/50 text-sm mt-1">{item.product.athlete}</p>
                    )}

                    <p className="font-barlow font-black text-xl text-white mt-3">
                      ${item.product?.price?.toFixed(2)}
                    </p>

                    {/* Quantity & Remove */}
                    <div className="flex items-center gap-4 mt-4">
                      {/* Quantity */}
                      <div className="flex items-center border border-[#27272A]">
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                          disabled={loading}
                          className="p-2 hover:bg-white/5 transition-colors duration-200 disabled:opacity-50"
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4 text-white" />
                        </button>
                        <span className="px-4 font-barlow font-bold text-white">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                          disabled={loading}
                          className="p-2 hover:bg-white/5 transition-colors duration-200 disabled:opacity-50"
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4 text-white" />
                        </button>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        disabled={loading}
                        className="p-2 text-white/50 hover:text-[#FF3B30] transition-colors duration-200 disabled:opacity-50"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Item Total */}
                  <div className="hidden md:block text-right">
                    <span className="font-barlow font-black text-xl text-white">
                      ${(item.product?.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#0A0A0A] border border-[#27272A] p-6 sticky top-28" data-testid="order-summary">
                <h3 className="font-barlow font-bold text-xl text-white mb-6">Order Summary</h3>

                <div className="space-y-4 pb-6 border-b border-[#27272A]">
                  <div className="flex justify-between">
                    <span className="text-white/60">Subtotal</span>
                    <span className="text-white font-medium">${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Shipping</span>
                    <span className="text-white font-medium">
                      {cart.total >= 250 ? 'Free' : 'Calculated at checkout'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between py-6">
                  <span className="font-barlow font-bold text-lg text-white">Total</span>
                  <span className="font-barlow font-black text-2xl text-white" data-testid="cart-total">
                    ${cart.total.toFixed(2)}
                  </span>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="checkout-btn"
                >
                  {loading ? 'Processing...' : 'Proceed to Checkout'}
                  <ArrowRight className="w-5 h-5" />
                </Button>

                <p className="text-white/40 text-xs text-center mt-4">
                  Secure checkout powered by Stripe
                </p>

                <Link to="/products" className="block text-center mt-6 text-white/60 hover:text-white transition-colors duration-200" data-testid="continue-shopping-link">
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
